"use client"

import type React from "react"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Box, CircularProgress, useMediaQuery, useTheme, AppBar, Toolbar, IconButton } from "@mui/material"
import { ArrowBack } from "@mui/icons-material"
import { useAuth } from "../../context/AuthContext"
import CustomAppBar from "../../components/shared/CustomAppBar"
import ConversationList from "../../components/chat/ConversationList"
import ConversationHeader from "../../components/chat/ConversationHeader"
import ChatContainer from "../../components/chat/ChatContainer"
import { useUserProfile } from "../../components/chat/ChatUserProfile"
import useChatSignalR from "../../components/chat/ChatSignalRService"
import type { ConversationDto } from "../../types/message"

const ChatPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>()
  const [tabValue, setTabValue] = useState(0)

  const navigate = useNavigate()
  const { userToken } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const { userProfileId } = useUserProfile({ userToken })

  const {
    conversations,
    selectedConversation,
    selectedConversationId,
    messages,
    loading,
    retryConnection,
    sendMessage,
    selectConversation,
  } = useChatSignalR({
    userId: userToken?.user?.id.toString() || "",
    userProfileId,
    conversationId,
  })

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  // Handle conversation selection
  const handleConversationClick = async (conversation: ConversationDto) => {
    await selectConversation(conversation)

    if (isMobile) {
      navigate(`/properties/messages/${conversation.conversationId}`)
    }
  }

  // Handle back button
  const handleBack = () => {
    if (isMobile && selectedConversation) {
      // En móvil, si hay una conversación seleccionada, volver a la lista
      navigate("/properties/messages", { replace: true })
    } else {
      // En otros casos, volver a la página anterior
      navigate(-1)
    }
  }

  // Get the other participant
  const otherParticipant = selectedConversation?.participants.find((p) => p.profileId !== userProfileId)

  // If there's no user token, show a loading state until the redirect happens
  if (!userToken) {
    return (
      <>
        <CustomAppBar />
        <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
          <CircularProgress color="success" />
        </Box>
      </>
    )
  }

  // En móvil, mostrar solo la lista de conversaciones o la conversación seleccionada
  if (isMobile) {
    // Si hay una conversación seleccionada, mostrar solo esa conversación
    if (selectedConversation) {
      return (
        <>
          <CustomAppBar />
          <AppBar position="fixed" sx={{ bgcolor: "white", color: "black", top: 56 }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={handleBack} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <ConversationHeader participant={otherParticipant} propertyInfo={selectedConversation.propertyInfo} />
            </Toolbar>
          </AppBar>

          <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "20px", marginTop: "120px", pb: "80px" }}>
            <ChatContainer
              selectedConversation={selectedConversation}
              messages={messages}
              userProfileId={userProfileId}
              onSendMessage={async (message) => {
                await sendMessage(message)
              }}
            />
          </Box>
        </>
      )
    }

    // Si no hay conversación seleccionada, mostrar la lista de conversaciones
    return (
      <>
        <CustomAppBar />
        <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "20px", marginTop: "64px" }}>
          <ConversationList
            conversations={conversations}
            selectedConversationId={selectedConversationId}
            userProfileId={userProfileId}
            loading={loading}
            error={null}
            tabValue={tabValue}
            onTabChange={handleTabChange}
            onConversationClick={handleConversationClick}
            onRetry={retryConnection}
            onBack={handleBack}
          />
        </Box>
      </>
    )
  }

  // En escritorio, mostrar la vista dividida
  return (
    <>
      <CustomAppBar />
      <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "20px", marginTop: "64px", display: "flex", gap: 3 }}>
        {/* Panel izquierdo - Lista de conversaciones */}
        <Box sx={{ width: "35%", minWidth: "300px" }}>
          <ConversationList
            conversations={conversations}
            selectedConversationId={selectedConversationId}
            userProfileId={userProfileId}
            loading={loading}
            error={null}
            tabValue={tabValue}
            onTabChange={handleTabChange}
            onConversationClick={handleConversationClick}
            onRetry={retryConnection}
            onBack={handleBack}
          />
        </Box>

        {/* Panel derecho - Conversación */}
        <Box sx={{ width: "65%", display: "flex", flexDirection: "column" }}>
          {selectedConversation && (
            <ConversationHeader participant={otherParticipant} propertyInfo={selectedConversation.propertyInfo} />
          )}
          <ChatContainer
            selectedConversation={selectedConversation}
            messages={messages}
            userProfileId={userProfileId}
            onSendMessage={async (message) => {
              await sendMessage(message)
            }}
          />
        </Box>
      </Box>
    </>
  )
}

export default ChatPage
